// src/api/aiStickyGenerationApi.js
import axios from 'axios';

const STICKY_GENERATION_API_URL = '/api/generate-stickies';

/**
 * Generate sticky notes from a user prompt using AI
 * @param {string} prompt - The user's prompt for generating stickies
 * @param {number} count - Number of stickies to generate (default: 3)
 * @returns {Promise<Object>} - The generated stickies data
 */
export const generateStickiesFromPrompt = async (prompt, count = 3) => {
  try {
    if (!prompt.trim()) {
      console.warn("No prompt provided for sticky generation.");
      return { stickies: [], count: 0 };
    }

    console.log("Calling Sticky Generation API with prompt:", prompt.substring(0, 100) + "...");
    console.log("Requested count:", count);
    
    const response = await axios.post(
      STICKY_GENERATION_API_URL,
      { 
        prompt: prompt.trim(),
        count: Math.max(1, Math.min(10, count)) // Ensure count is between 1-10
      },
      {
        withCredentials: true, // Sends authentication cookies automatically
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log("Sticky Generation API response received:", {
      stickyCount: response.data.stickies?.length || 0,
      tokenUsage: response.data.token_usage
    });
    
    // Validate response structure
    const result = {
      stickies: response.data.stickies || [],
      count: response.data.count || 0,
      token_usage: response.data.token_usage || 0
    };
    
    // Ensure each sticky has the required structure
    result.stickies = result.stickies.map((sticky, index) => ({
      title: sticky.title || `Generated Sticky ${index + 1}`,
      content: sticky.content || "Generated content"
    }));
    
    return result;
  } catch (error) {
    console.error("Error generating stickies:", error.message);
    
    // Match error handling from aiKeyPointsApi.js
    if (error.response?.status === 402) {
      console.warn("Token balance error");
      throw new Error("Insufficient token balance");
    }

    if (error.response?.status === 401) {
      console.warn("Authentication error");
      throw new Error("Authentication failed");
    }

    // Return empty result for other errors
    throw new Error("Sticky generation failed");
  }
};